import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import type { BuiltPath } from '../../lib/buildMapPaths';
import DistrictMap from './DistrictMap';
import DistrictPanel from './DistrictPanel';
import './MapFullscreen.css';

interface Props {
  active: number | null;
  onSelect: (id: number) => void;
  onClose: () => void;
  district: BuiltPath | null;
}

export default function MapFullscreen({ active, onSelect, onClose, district }: Props) {
  // Lock background scroll while the modal is open so scrolling inside
  // the modal doesn't leak through to the dashboard underneath.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  // ESC closes — standard modal affordance.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return createPortal(
    <div
      className="map-fullscreen"
      role="dialog"
      aria-modal="true"
      aria-label="Malta 2022 election results — full-screen map"
    >
      <header className="map-fullscreen__header">
        <div>
          <p className="map-fullscreen__kicker">2022 Results</p>
          <h2 className="map-fullscreen__title">Malta by district</h2>
        </div>
        <button
          type="button"
          className="map-fullscreen__close"
          onClick={onClose}
          aria-label="Close fullscreen map"
        >
          <span aria-hidden="true">×</span>
        </button>
      </header>

      <div className="map-fullscreen__body">
        <div className="map-fullscreen__map">
          <DistrictMap active={active} onSelect={onSelect} />
        </div>
        <div className="map-fullscreen__panel">
          <DistrictPanel district={district} />
        </div>
      </div>
    </div>,
    document.body,
  );
}
